import Analytics from './analyticsInterface';

interface DynamicEvent {
    eventName: string;
    analyticItems: AnalyticsItem[];
}

interface AnalyticsItem {
    name: string;
    value: string | NestedValue | NestedValue[];
}

interface NestedValue {
    [key: string]: string | NestedValue | NestedValue[];
}

// Declare gtag function from the global scope
declare global {
    interface Window {
        adobeDataLayer: { [key: string]: any }[];
    }
}

export class AemTracker implements Analytics {
    track(trackingData?: { dynamicEvents?: DynamicEvent[] }): void {
        // Implement logic to send events to Adobe Analytics Manager
        if (trackingData && trackingData.dynamicEvents) {
            trackingData.dynamicEvents.forEach((dynamicEvent: DynamicEvent) => {
                const { eventName, analyticItems } = dynamicEvent;
                const eventTrackingData: Record<string, any> = {};

                analyticItems?.forEach((item) => {
                    if (
                        (typeof item.value === 'string' &&
                            item.value.startsWith('{') &&
                            item.value.endsWith('}')) ||
                        (typeof item.value === 'string' &&
                            item.value.startsWith('[') &&
                            item.value.endsWith(']'))
                    ) {
                        const parsedValue = JSON.parse(item.value);
                        eventTrackingData[item.name] = this.flattenNestedValue(parsedValue);
                    } else {
                        eventTrackingData[item.name] = item.value;
                    }
                });

                const trackingData = {
                    event: eventName,
                    ...eventTrackingData,
                };

                // Ensure window.adobeDataLayer is initialized
                if (!window.adobeDataLayer) {
                    window.adobeDataLayer = [];
                }

                window.adobeDataLayer.push(trackingData);
            });
        }
    }

    private flattenNestedValue(nestedValue: NestedValue | NestedValue[]): NestedValue[] {
        if (Array.isArray(nestedValue)) {
            // If nestedValue is an array, flatten each item recursively
            return nestedValue.flatMap((item) => this.flattenNestedValue(item));
        }

        const flattened: NestedValue = {};

        Object.keys(nestedValue).forEach((key) => {
            const value = nestedValue[key];
            if (typeof value === 'object') {
                flattened[key] = this.flattenNestedValue(value);
            } else {
                flattened[key] = value;
            }
        });

        return [flattened]; // Wrap the flattened object in an array
    }
}
